import React from 'react';
import clsx from 'clsx';
import { alpha, makeStyles, Theme } from '@material-ui/core';
import {
    blue,
    cyan,
    deepPurple,
    orange,
    pink,
    teal,
} from '@material-ui/core/colors';
import { ILabel } from './types';
import {
    EXTRA_HOT_COLOR,
    HOT_COLOR,
    WARM_COLOR,
    COLD_COLOR,
    NONE_COLOR,
} from 'src/constants/lead/ratings';

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        fontFamily: theme.typography.fontFamily,
        alignItems: 'center',
        borderRadius: 2,
        display: 'inline-flex',
        flexGrow: 0,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 700,
        height: 20,
        justifyContent: 'center',
        letterSpacing: 0.5,
        minWidth: 20,
        padding: theme.spacing(0.5, 1),
        cursor: 'pointer',
    },
    primary: {
        color: theme.palette.primary.main,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    secondary: {
        color: theme.palette.secondary.main,
        backgroundColor: alpha(theme.palette.secondary.main, 0.08),
    },
    green: {
        color: theme.palette.success.main,
        backgroundColor: alpha(theme.palette.success.main, 0.08),
    },
    yellow: {
        color: theme.palette.warning.main,
        backgroundColor: alpha(theme.palette.warning.main, 0.08),
    },
    red: {
        color: theme.palette.error.main,
        backgroundColor: alpha(theme.palette.error.main, 0.08),
    },
    blue: {
        color: blue[600],
        backgroundColor: alpha(blue[600], 0.08),
    },
    deepPurple: {
        color: deepPurple[500],
        backgroundColor: alpha(deepPurple[500], 0.08),
    },
    white: {
        color: theme.palette.common.white,
        backgroundColor: alpha(theme.palette.common.white, 0.08),
    },
    extraHot: {
        color: EXTRA_HOT_COLOR,
        backgroundColor: alpha(EXTRA_HOT_COLOR, 0.08),
    },
    hot: {
        color: HOT_COLOR,
        backgroundColor: alpha(HOT_COLOR, 0.08),
    },
    warm: {
        color: WARM_COLOR,
        backgroundColor: alpha(WARM_COLOR, 0.08),
    },
    cold: {
        color: COLD_COLOR,
        backgroundColor: alpha(COLD_COLOR, 0.08),
    },
    none: {
        color: NONE_COLOR,
        backgroundColor: alpha(NONE_COLOR, 0.08),
    },
    cyan: {
        color: cyan[600],
        backgroundColor: alpha(cyan[600], 0.08),
    },
    orange: {
        color: orange[600],
        backgroundColor: alpha(orange[600], 0.08),
    },
    pink: {
        color: pink[600],
        backgroundColor: alpha(pink[600], 0.08),
    },
    darkTeal: {
        color: teal[600],
        backgroundColor: alpha(teal[600], 0.08),
    },
}));

export const Label = ({
    className = '',
    color,
    children,
    ...props
}: ILabel) => {
    const classes = useStyles();

    const LABEL_COLORS: { [TLabelColor: string]: string } = {
        primary: classes.primary,
        secondary: classes.secondary,
        success: classes.green,
        warning: classes.yellow,
        error: classes.red,
        'extra hot': classes.extraHot,
        hot: classes.hot,
        warm: classes.warm,
        cold: classes.cold,
        none: classes.none,
        low: classes.blue,
        normal: classes.yellow,
        urgent: classes.red,
        pending: classes.red,
        progress: classes.yellow,
        white: classes.white,
        cyan: classes.cyan,
        darkTeal: classes.darkTeal,
        orange: classes.orange,
        pink: classes.pink,
        blue: classes.blue,
    };

    return (
        <span
            className={clsx(classes.root, LABEL_COLORS[color], className)}
            {...props}
        >
            {children}
        </span>
    );
};
